import React from 'react';
import { DesignHeader } from './DesignHeader';
import { PageTitle } from '../ui/PageTitle';
import { LeadParagraph } from '../ui/LeadParagraph';
import { BodyParagraph } from '../ui/BodyParagraph';
import { HighlightTag } from '../ui/HighlightTag';
import { ContentLayout } from '../ui/ContentLayout';
import { ExternalLink } from '../ui/ExternalLink';
import { ImageModule } from '../ui/ImageModule';
import { PageMeta } from './PageMeta';
export function DesignContent({
  activePage,
  onNavigate,
  onOpenMobileMenu
}: {
  activePage: string;
  onNavigate: (page: string) => void;
  onOpenMobileMenu: () => void;
}) {
  const renderContent = () => {
    switch (activePage) {
      case 'research':
        return <>
            <PageMeta title="Research & Decisions | Wojtek Swiatoniowski" description="I integrate discovery directly into delivery cycles, turning qualitative signals into prioritised decisions that shape what gets built." data-id="element-11" />
            <PageTitle data-id="element-12">
              Research is only useful if it changes what gets built.
            </PageTitle>
            <div className="space-y-8" data-id="element-13">
              <LeadParagraph data-id="element-14">
                I integrate discovery directly into delivery cycles, rather than
                seeing it as a preliminary step before actual work.
              </LeadParagraph>
              <BodyParagraph data-id="element-15">
                At Lendi Group, this meant fortnightly field studies embedded in
                sprint cycles, including a June 2024 study where two other
                designers and I spent 23 hours with 30 brokers and support
                staff, generating 384 feedback points that directly shaped
                product decisions. I intentionally involve engineers in these
                sessions because the empathy built during contextual interviews
                changes how technical trade-offs are made during sprint
                planning.
              </BodyParagraph>
              <div className="my-12 animate-fadeIn" data-id="element-16">
                <div className="rounded-lg overflow-hidden" style={{
                aspectRatio: '4/3'
              }} data-id="element-17">
                  <img src="https://cdn.magicpatterns.com/uploads/e8HGASn8AXvEBkhrV9NWbQ/Slide_2.svg" alt="Broker Field Study — 384 feedback points collected from 30 brokers in a single study" className="w-full h-full object-cover" data-id="element-18" />
                </div>
              </div>
              <BodyParagraph data-id="element-19">
                At Shift, research informs a bigger question: how behavioural
                and operational signals, such as time in queue, customer and
                broker activity patterns, and system integration failures,
                predict which deals are at risk of abandonment before they
                stall? The goal is to move from reactive UX fixes to a
                predictive, data-driven model that steers different customer
                archetypes toward the right product at the right time, with
                minimal friction. Abandonment isn't a front-end problem; it's
                about queue design, workflow intelligence, and partner dynamics.
              </BodyParagraph>
              <BodyParagraph data-id="element-20">
                The Opportunity Solution Tree keeps this work honest,
                translating messy qualitative signals into a prioritised
                structure that product and business teams can interrogate and
                challenge, rather than just receive.
              </BodyParagraph>
              <BodyParagraph data-id="element-21">
                I've also introduced AI-assisted workflows to change how
                insights move through an organisation, not just how interfaces
                are made. At Lendi Group, that meant a passive idea bank in Jira
                Product Discovery to capture and categorise broker feedback at
                scale, and a Rovo AI agent generating regular report summaries
                to guide refinement priorities. For prototyping, MagicPatterns
                and v0 compress the gap between a rough hypothesis and a
                testable outcome, and extract acceptance criteria directly from
                design artefacts.{' '}
                <HighlightTag data-id="element-22">
                  The goal across all of it is the same: reduce the friction
                  between a signal and a decision.
                </HighlightTag>
              </BodyParagraph>
            </div>
          </>;
      case 'systems':
        return <>
            <PageMeta title="Systems That Last | Wojtek Swiatoniowski" description="I anchor work in real workflows and measurable outcomes, so it holds its shape after handover, headcount changes, and shifting priorities." data-id="element-23" />
            <PageTitle data-id="element-24">
              Good delivery is measured by what remains after you leave.
            </PageTitle>
            <div className="space-y-8" data-id="element-25">
              <LeadParagraph data-id="element-26">
                I anchor work in real workflows and measurable outcomes from the
                start, so it holds its shape after handover, headcount changes,
                and shifting priorities.
              </LeadParagraph>
              <BodyParagraph data-id="element-27">
                At Shift, I defined the product concept and cross-functional
                scope for Property Hub, a single source of truth for end-to-end
                lending efficiency. This work was grounded in detailed process
                mapping across Credit, Business Services, Settlements, and
                Collections, with measurable outcomes set from the start: time
                saved per assessment, reduced platform switches (aiming to
                decrease from four sources to two per credit assessment),
                automated equity calculation, and more reliable data as primary
                success indicators.{' '}
                <HighlightTag data-id="element-28">
                  Anchoring the product in real workflows rather than feature
                  requests gives it a sustainable long-term direction.
                </HighlightTag>
              </BodyParagraph>
              <BodyParagraph data-id="element-29">
                At Lendi Group, the{' '}
                <ExternalLink href="https://www.theadviser.com.au/broker/46899-lendi-group-launches-sandbox-platform-to-support-brokers?highlight=WyJzYW5kYm94Il0=" data-id="element-30">
                  Servicing Sandbox
                </ExternalLink>{' '}
                launched across the network in March 2025, following a pilot
                with 100 staff in November 2024. I used feature maps at the user
                story level to explore variations and edge cases early,
                validating assumptions through that pilot before full rollout.
                In the first week, 1,100 brokers ran 7,000 scenarios, with
                nearly 70% returning.
              </BodyParagraph>
              <div className="my-12 animate-fadeIn" data-id="element-31">
                <div className="rounded-lg overflow-hidden" style={{
                aspectRatio: '4/3'
              }} data-id="element-32">
                  <img src="https://cdn.magicpatterns.com/uploads/u6sZKtUbYGfjrCMCMEnRQA/Slide_3.svg" alt="Servicing Sandbox — 7,000+ scenarios run by 1,100 brokers in week one, with nearly 70% returning" className="w-full h-full object-cover" data-id="element-33" />
                </div>
              </div>
              <BodyParagraph data-id="element-34">
                One thing I'd do differently at Lendi is build organisational
                influence sooner. That lesson now shapes how I engage
                stakeholders, including my current efforts to develop strategic
                partnerships with Shift's senior leadership to influence
                workflow, prioritisation, and the product experience across the
                business.
              </BodyParagraph>
            </div>
          </>;
      case 'complexity':
      default:
        return <>
            <PageMeta title="The Problem Behind the Problem | Wojtek Swiatoniowski" description="In fintech, products and workflows are interconnected systems, not linear flows. I map the constraints before designing the interface." data-id="element-35" />
            <PageTitle data-id="element-36">
              In fintech, products and workflows are interconnected systems, not
              linear flows.
            </PageTitle>
            <div className="space-y-8" data-id="element-37">
              <LeadParagraph data-id="element-38">
                The policy decisions, technological constraints, and human
                judgements that build up across origination, decisioning, and
                servicing shape what users actually experience. My work starts
                at this foundational level, before the interface.
              </LeadParagraph>
              <BodyParagraph data-id="element-39">
                At Shift, this means mapping handoffs, queue behaviours, and
                manual workarounds across Credit, Broker Operations, and
                Settlements to understand where commercial leakage occurs and
                where workflow design leads to abandonment rather than
                conversion. At Lendi Group, before redesigning Required Actions,
                I audited the existing policy rules and eliminated 285
                duplicates, raising the threshold for adding new ones entirely.
                The UI work came second. This sequence prevented months of
                downstream debt, reframed what the team was actually trying to
                solve, and ultimately saved over 13,000 broker minutes each
                month.
              </BodyParagraph>
              <div className="my-12 animate-fadeIn" data-id="element-40">
                <div className="rounded-lg overflow-hidden" style={{
                aspectRatio: '4/3'
              }} data-id="element-41">
                  <img src="https://cdn.magicpatterns.com/uploads/6ad4CNyysTrpZ6q3CrKNy5/Slide_1.svg" alt="Required Actions — 285 duplicate policy rules audited and removed before a single screen was redesigned" className="w-full h-full object-cover" data-id="element-42" />
                </div>
              </div>
              <BodyParagraph data-id="element-43">
                <HighlightTag data-id="element-44">
                  My three guiding principles are: identify the real constraint
                  before proposing a solution, design for the person doing the
                  hardest version of the task, and make the rationale clear so
                  everyone in the room can challenge it, not just the designers.
                </HighlightTag>
              </BodyParagraph>

              <BodyParagraph data-id="element-45">
                These principles only hold if the people around you share them.
                At Lendi Group and Shift, I set up weekly Product Quad Think
                Tanks and biweekly Jam Forums, open to the entire
                cross-functional team, to identify friction and opportunities
                before they became assumptions. The measure was never
                attendance. It was whether the quality of questions asked in
                sprint planning improved over time. It did.
              </BodyParagraph>
            </div>
          </>;
    }
  };
  return <div className="flex-1 bg-white text-black flex flex-col min-h-screen" data-id="element-46">
      <DesignHeader onNavigate={onNavigate} onOpenMobileMenu={onOpenMobileMenu} data-id="element-47" />

      <div className="flex-1 overflow-y-auto" data-id="element-48">
        <ContentLayout data-id="element-49">{renderContent()}</ContentLayout>
      </div>
    </div>;
}